import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, IconButton, CircularProgress, Alert, Toolbar, TextField, InputAdornment, Typography
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import api from '../services/api';
import SupportModal from './SupportModal';

const SuportTable = () => {
  const [data, setData] = useState({
    supports: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentSupport, setCurrentSupport] = useState(null);

  const fetchSupports = useCallback(async () => {
    try {
      const response = await api.get(`/suportt`);
      setData({
        supports: response.data
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch supports', error);
      setError('Failed to fetch supports');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSupports();
  }, [fetchSupports]);

  const handleAddSupport = () => {
    setCurrentSupport(null);
    setModalOpen(true);
  };

  const handleEditSupport = (support) => {
    setCurrentSupport(support);
    setModalOpen(true);
  };

  const handleDeleteSupport = (support) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: `Deseja realmente excluir o suporte ${support.email}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/suportt/delete-support/${support.id}`);
          setData((prevData) => ({
            ...prevData,
            supports: prevData.supports.filter((s) => s.id !== support.id)
          }));
          Swal.fire('Excluído!', 'O suporte foi excluído.', 'success');
          fetchSupports();
        } catch (error) {
          Swal.fire('Erro!', 'Falha ao excluir o suporte.', 'error');
        }
      }
    });
  };

  const handleSaveSupport = async (support) => {
    try {
      if (support.id) {
        await api.put(`/suportt/update-support-email/${support.id}`, { email: support.email });
        await api.put(`/suportt/update-support-wpp/${support.id}`, { wpp: support.wpp });
        await api.put(`/suportt/update-support-sites/${support.id}`, { sites: support.sites });
        await api.put(`/suportt/update-support-phone/${support.id}`, { phone: support.phone });
        setData((prevData) => ({
          ...prevData,
          supports: prevData.supports.map((s) => (s.id === support.id ? support : s))
        }));
      } else {
        const response = await api.post('/suportt/create-support', support);
        setData((prevData) => ({
          ...prevData,
          supports: [response.data, ...prevData.supports]
        }));
      }
      setModalOpen(false);
      Swal.fire('Sucesso!', 'Suporte salvo com sucesso.', 'success');
      fetchSupports();
    } catch (error) {
      Swal.fire('Erro!', 'Falha ao salvar o suporte.', 'error');
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredSupports = data.supports ? data.supports.filter(support =>
    support.email && support.email.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Box className="supports-container" sx={{ width: '50%', padding: 3, backgroundColor: 'white', borderRadius: 2, boxShadow: 3, fontFamily: 'Roboto' }}>
        <Typography variant="h4" sx={{ textAlign: 'center', marginBottom: 3 }}>Lista de Suportes</Typography>
        <Toolbar sx={{ height: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
          <TextField
            placeholder="Buscar..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: '30%', backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { fontFamily: 'Roboto' },
            }}
            InputLabelProps={{ style: { fontFamily: 'Roboto' } }}
          />
          <IconButton color="primary" aria-label="add support" onClick={handleAddSupport} style={{ marginLeft: '10px' }}>
            <AddIcon />
          </IconButton>
        </Toolbar>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '100%' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">Email</TableCell>
                <TableCell align="center">WhatsApp</TableCell>
                <TableCell align="center">Sites</TableCell>
                <TableCell align="center">Telefone</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSupports.map((support) => (
                <TableRow key={support.id}>
                  <TableCell align="center">{support.id}</TableCell>
                  <TableCell align="center">{support.email}</TableCell>
                  <TableCell align="center">{support.wpp}</TableCell>
                  <TableCell align="center">{support.sites}</TableCell>
                  <TableCell align="center">{support.telefone}</TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" aria-label="edit support" onClick={() => handleEditSupport(support)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="secondary" aria-label="delete support" onClick={() => handleDeleteSupport(support)} style={{ marginLeft: '10px' }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <SupportModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSaveSupport} support={currentSupport} />
    </Box>
  );
};

export default SuportTable;
